import { useEffect, useRef } from 'react';
import { Check, X } from 'lucide-react';

interface ThankYouModalProps {
  open: boolean;
  onClose: () => void;
}

export function ThankYouModal({ open, onClose }: ThankYouModalProps): React.JSX.Element | null {
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  // Close on Escape; lock body scroll while open; focus the close button.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeBtnRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="thanks-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="thanks-title"
      onClick={onClose}
    >
      <div className="thanks-card" onClick={(e) => e.stopPropagation()}>
        <button
          ref={closeBtnRef}
          type="button"
          className="thanks-close"
          aria-label="Close"
          onClick={onClose}
        >
          <X size={16} />
        </button>

        <div className="thanks-check" aria-hidden="true">
          <Check size={28} strokeWidth={2.5} />
        </div>

        <h3 id="thanks-title" className="thanks-title">
          Thank you — we got it.
        </h3>
        <p className="thanks-body">
          We'll review what you sent and reply within one business day at the email you
          provided.
        </p>

        <div className="thanks-actions">
          <button type="button" className="btn btn-primary btn-lg" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
